<template>
    <v-form
        v-model="valid"
        ref="form"
        :disabled="props.disabled || isSubmitting"
        :readonly="readonlyFields"
        :loading="isSubmitting || isLoadingIndicators"
    >
        <fieldset class="border-0" >
            <legend class="text-h4 mt-3 mb-6">  Competência  </legend>
            <v-row
                v-for="(localizedCompetency, index) in formValues.competency.localizations"
                :key="index"
                class="mx-0 my-4 mb-8"
            >
                <v-col class="pa-0 pe-md-2" cols="12" md="3">
                    <v-autocomplete
                        v-model="localizedCompetency.locale_id"
                        label="Língua / Localização"
                        :items="getAvailableIndicatorLocalizations(localizedCompetency.locale_id)"
                        item-value="id"
                        :item-title="(item) => `${item.name} (${item.code})`"
                        :readonly="readonlyFields"
                        :clearable="!readonlyFields"
                        :menu-icon="readonlyFields ? '' : undefined"
                        autocomplete="off"
                        :disabled="props.disabled || isSubmitting || isLoadingLocales"
                        :rules="[
                            formValidationRules.required(localizedCompetency.locale_id),
                        ]"
                    />
                    
                </v-col>
                <v-col
                    cols="12" 
                    md="9"
                    class="pa-0 ps-md-2" 
                    >
                    <v-text-field
                        v-if="showsCompetencyName"
                        v-model="localizedCompetency.name"
                        label="Nome"
                        :readonly="readonlyFields"
                        :disabled="props.disabled || isSubmitting || isLoadingIndicators"
                        :loading="isSubmitting || isLoadingIndicators"
                        :clearable="readonlyFields"
                        :validation-value="competencyTextValidationValue(localizedCompetency)"
                        :rules="[
                            () => requiresCompetencyText(
                                localizedCompetency.name,
                                localizedCompetency.description,
                            )
                        ]"
                        autocomplete="off"
                    />
                    <v-textarea
                        v-if="showsCompetencyDescription"
                        v-model="localizedCompetency.description"
                        label="Descrição"
                        :disabled="props.disabled || isSubmitting || isLoadingIndicators"
                        :loading="isSubmitting || isLoadingIndicators"
                        :clearable="readonlyFields"
                        :validation-value="competencyTextValidationValue(localizedCompetency)"
                        :rules="[
                            () => requiresCompetencyText(
                                localizedCompetency.name,
                                localizedCompetency.description,
                            )
                        ]"
                        autocomplete="off"
                    />
                </v-col>
                <div class="d-flex justify-end mt-2 flex-grow-1">

                    <v-btn 
                        v-if="formValues.competency.localizations?.length > 1 && !readonlyFields"
                        color="error" 
                        prepend-icon="mdi-delete" 
                        variant="outlined" 
                        class="text-none"
                        :disabled="isSubmitting || isLoadingIndicators"
                        @click="formValues.competency.localizations?.splice(index,1)"
                    >
                        Remover
                    </v-btn>
                </div>
            </v-row>
            <v-row class="mx-auto mt-8 mb-4 justify-center">
                <v-btn 
                    v-if="!readonlyFields"
                    class="text-none" 
                    color="success" 
                    variant="outlined"
                    :disabled="isSubmitting || isLoadingIndicators"
                    @click="formValues.competency.localizations.push({...competencyLocalizationTemplate})"
                >
                    <template #prepend>
                        <v-icon>mdi-plus</v-icon>
                        <v-icon>mdi-translate</v-icon>
                    </template>
                    Traduzir Competência
                </v-btn>
            </v-row>
            <hr class="mt-4 mb-8">
        </fieldset>
        <fieldset class="border-0">
            <legend class="text-h4  mt-3 mb-6">Indicadores</legend>
            
            <div 
                v-for="(localizedIndicator, indicatorIndex) in formValues.competency?.indicators"
                :key="localizedIndicator.clientKey ?? localizedIndicator.id ?? indicatorIndex"
                class="d-flex flex-grow-1 flex-column"
            >
                <IndicatorForm
                    :indicator="localizedIndicator"
                    :indicator-type="props.competencyType"
                    :loading-locales="isLoadingLocales"
                    :locales-list="localesList"
                    :readonly="readonlyFields"
                    :indicator-label="`Indicador #${indicatorIndex+1}`"
                    :is-loading-indicators="isLoadingIndicators"
                    @update:indicator="formValues.competency.indicators[indicatorIndex] = $event"
                    @remove-indicator="formValues.competency?.indicators?.splice(indicatorIndex, 1)"
                    :show-delete-indicator-button="formValues.competency?.indicators?.length > 1"
                    :submitting="isSubmitting"
                />
                
                <hr v-if="formValues.competency?.indicators?.length > 1">
            </div>
            
            <v-row class="mx-auto mt-8 mb-4 justify-center">
                <v-btn 
                    v-if="!readonlyFields"
                    class="text-none" 
                    color="success" 
                    variant="flat"
                    :disabled="isSubmitting || isLoadingIndicators"
                    @click="formValues.competency.indicators.push(createIndicator())"
                >
                    <template #prepend>
                        <v-icon>mdi-plus</v-icon>
                        <v-icon>mdi-list-status</v-icon>
                    </template>
                    Adicionar Indicador
                </v-btn>
                   
            </v-row>
        </fieldset>
    </v-form>
</template>
<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import formValidationRules from '../../../composables/formValidationRules';
import IndicatorForm from './IndicatorForm.vue';
import Locales from "@/api/locales/Locales";
import TechnicalCompetencies from '../../../api/assessments/competencies/TechnicalCompetencies.js';
import PersonalCompetencies from '../../../api/assessments/competencies/PersonalCompetencies.js';


const props = defineProps({
    competencyType : {
        type: String,
        required: true,
    },
    onSubmit: {
        type: Function,
        default: null,
    },
    action : {
        type: String,
        default: "create",
    },
    competency : {
        type: Object,
        default: null,
    },
    competencyId : {
        type: String,
        default: null,
    },
    disabled : {
        type: Boolean,
        default: false,
    },
    readonly : {
        type: Boolean,
        default: false
    },
    idempotencyKey : {
        type: String,
        default: null,
    },
    competencyHasName: {
        type: Boolean,
        default: true,
    },
    competencyHasDescription : {
        type: Boolean,
        default: true,
    },
    competencyHasIndicators : {
        type: Boolean,
        default: true,
    },

});

defineExpose({
    validate,
    submit,
});

const competencyLocalizationTemplate = {
    locale_id: null,
    name: null,
    description: null,
};


const competencyIndicatorLocalizationTemplate = {
    locale_id: null,
    name: null,
    description: null,
};

function createIndicator() {
    return {
        clientKey: crypto.randomUUID(),
        id: null,
        localizations: [
            {
                locale_id: null,
                name: null,
                description: null,
            },
        ],

    }
}

const competencyIndicatorTemplate = {
    id: null,
    localizations : [
        {...competencyIndicatorLocalizationTemplate},
    ],
};

const formValues = ref({
    competency: createEmptyCompetency(),
});

const form = ref("form");
const valid = ref(false);
const isSubmitting = ref(false);
const localesList = ref([]);
const isLoadingLocales = ref(false);
const indicatorsList = ref([]);
const isLoadingIndicators = ref(false);

const readonlyFields = props.readonly 
                        || ![
                            "new",
                            "create",
                            "update",
                            "edit",
                        ].includes(props.action);

const isTechnicalCompetency = computed(() => {
    return props.competencyType === "technical";
});

const showsCompetencyName = computed(() => {
    return isTechnicalCompetency.value || props.competencyHasName;
});

const showsCompetencyDescription = computed(() => {
    return !isTechnicalCompetency.value && props.competencyHasDescription;
});



const competencyTextValidationValue = (localizedCompetency) => {
    return JSON.stringify([
        localizedCompetency.name,
        localizedCompetency.description,
    ]);
};

const requiresCompetencyText = (name, description) => {
    return (
        !formValidationRules.isEmpty(name)
        || !formValidationRules.isEmpty(description)
        || "Preencha o nome ou a descrição da competência."
    );
};

const getAvailableIndicatorLocalizations = (currentLocaleId) => {
    const selectedLocaleIds = new Set(
        formValues.value.competency.localizations
            .map((localized) => localized.locale_id)
            .filter(Boolean),
    );

    return localesList.value.filter((locale) => {
        return (
            locale.id === currentLocaleId
            || !selectedLocaleIds.has(locale.id)
        );
    });
};

function createEmptyCompetency() {
    return {
        id: props.competencyId ?? null,
        localizations: [
            {
                ...competencyLocalizationTemplate,
            },
        ],
        indicators: [
            createIndicator(),
        ],
    };
}

function normalizeCompetency(competency) {
    const localizations = competency.localizations
        ?? competency.translations
        ?? [];

    return {
        id: competency.id,
        number: competency.number ?? null,
        localizations: localizations.map((localization) => ({
            locale_id: localization.locale_id,
            name: localization.name ?? null,
            description: localization.description ?? null,
        })),
        indicators: (competency.indicators ?? []).map((indicator) => ({
            id: indicator.id,
            number: indicator.number ?? null,
            clientKey: crypto.randomUUID(),
            localizations: (indicator.localizations ?? indicator.translations ?? []).map((localization) => ({
                locale_id: localization.locale_id,
                name: localization.name ?? null,
                description: localization.description ?? null,
            })),
        })),
    };
}

function resolveCompetenciesApi() {
    return isTechnicalCompetency.value
        ? TechnicalCompetencies
        : PersonalCompetencies;
}

async function hydrateCompetency() {
    const competencyId = props.competency?.id ?? props.competencyId;

    if (
        !competencyId
        || ["create", "new",].includes(props.action)
    ) {
        return;
    }

    isLoadingIndicators.value = true;

    try {
        const response = await resolveCompetenciesApi().show(competencyId);

        formValues.value.competency = normalizeCompetency(response.data);
    } finally {
        isLoadingIndicators.value = false;
    }
}

async function fetchLocalesList() {
    isLoadingLocales.value = true;

    Locales.all()
    .then((response) => {
        if(
            Array.isArray(response?.data)
            && response?.data?.length > 0
        ) {
            localesList.value = [
                ...response.data
            ];
        }
    })
    .catch((error) => {
        console.error(error);
    })
    .finally(() => {
        isLoadingLocales.value = false;
    })
}

async function validate() {
    const result = await form.value.validate();

    return result.valid;
}

async function submit() {
    isSubmitting.value = true;
    
    const shouldValidate = [
        "create",
        "new",
        "update",
        "edit",
    ].includes(props.action) && !props.disabled;

    try {

        if(shouldValidate) {
            const passedValidation = await validate();

            if (!passedValidation) {
                console.warn(
                    "Validação falhou no CompetencyForm",
                    formValues
                );

                return false;
            }
        }

        if (props.onSubmit) {
            return await props.onSubmit(formValues.value.competency);
        }

        const api = resolveCompetenciesApi();
        const payload = formValues.value.competency;

        switch (props.action) {
            case "create":
            case "new":
                return api.create(payload);

            case "update":
            case "edit":
                return api.update(payload.id, payload);

            case "delete":
                return api.delete(payload.id);

            default:
                throw new RangeError(`Unsupported competency action: ${props.action}.`);
        }
        
    } catch (error) {

        console.error(
            "Erro ao submeter CompetencyForm:",
            error
        );

        throw error;
        
    } finally {
        isSubmitting.value = false;
    }

}

onBeforeMount(async () => {
    if (!showsCompetencyName.value && !showsCompetencyDescription.value) {
        throw new RangeError("Competencies must have at least one of: name, description.");
    }
    await Promise.all([
        fetchLocalesList(),
        hydrateCompetency(),
    ]);
});
</script>
